/**
 * Provision Number Dialog
 *
 * Two-pane flow:
 *  1. Search: form for criteria + live results from Telnyx /v2/available_phone_numbers
 *  2. Confirm: chosen number + friendly-name + set-as-default + final order
 *
 * Designed as a single dialog with internal step state — operators don't lose
 * their search criteria when reviewing a number, and Esc cancels the whole thing.
 */
import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Label,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  cn,
} from '@respectly/ui';
import {
  Search,
  Phone,
  ArrowLeft,
  Loader2,
  AlertCircle,
  MapPin,
  CheckCircle2,
  Sparkles,
} from 'lucide-react';
import type {
  TelnyxAvailableNumber,
  TelnyxNumberSearchCriteria,
} from '@respectly/shared-types';

interface ProvisionNumberDialogProps {
  open: boolean;
  onClose: () => void;
  onSearch: (criteria: TelnyxNumberSearchCriteria) => Promise<void>;
  onProvision: (params: {
    number: string;
    friendlyName?: string;
    setAsDefault?: boolean;
  }) => Promise<boolean>;
  searchResults: TelnyxAvailableNumber[];
  isSearching: boolean;
  isProvisioning: boolean;
  searchError: { code: string; message: string } | null;
  /** True only for the 10DLC route — gates the campaign-attached warning banner. */
  campaignReady: boolean;
  /** Pre-select the number type when the dialog opens. */
  defaultType?: 'local' | 'tollfree';
  /** When true, the campaign warning banner is hidden (e.g. toll-free route). */
  hideCampaignWarning?: boolean;
}

function formatE164(num: string): string {
  const m = num.match(/^\+1(\d{3})(\d{3})(\d{4})$/);
  if (m) return `+1 (${m[1]}) ${m[2]}-${m[3]}`;
  return num;
}

export function ProvisionNumberDialog({
  open,
  onClose,
  onSearch,
  onProvision,
  searchResults,
  isSearching,
  isProvisioning,
  searchError,
  campaignReady,
  defaultType = 'local',
  hideCampaignWarning = false,
}: ProvisionNumberDialogProps) {
  const [step, setStep] = useState<'search' | 'confirm'>('search');
  const [areaCode, setAreaCode] = useState('');
  const [contains, setContains] = useState('');
  const [type, setType] = useState<'local' | 'tollfree'>(defaultType);
  const [selected, setSelected] = useState<TelnyxAvailableNumber | null>(null);
  const [friendlyName, setFriendlyName] = useState('');
  const [setAsDefault, setAsDefaultState] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Re-seed the number type whenever the dialog opens so route changes take effect.
  useEffect(() => {
    if (open) setType(defaultType);
  }, [open, defaultType]);

  const handleClose = () => {
    setStep('search');
    setSelected(null);
    setFriendlyName('');
    setAsDefaultState(false);
    setHasSearched(false);
    onClose();
  };

  const handleSearch = async () => {
    if (!areaCode && !contains && type === 'local') {
      // Need at least something for local
      return;
    }
    setHasSearched(true);
    await onSearch({
      country: 'US',
      areaCode: areaCode || undefined,
      contains: contains || undefined,
      type,
      capabilities: ['sms'],
      limit: 20,
    });
  };

  const handleSelect = (num: TelnyxAvailableNumber) => {
    setSelected(num);
    setStep('confirm');
  };

  const handleProvision = async () => {
    if (!selected) return;
    const ok = await onProvision({
      number: selected.number,
      friendlyName: friendlyName.trim() || undefined,
      setAsDefault,
    });
    if (ok) handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            {step === 'confirm' && (
              <button
                type="button"
                onClick={() => setStep('search')}
                className="flex h-7 w-7 items-center justify-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
                aria-label="Back to search"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                {step === 'search' ? 'Step 1 of 2' : 'Step 2 of 2'}
              </p>
              <DialogTitle className="mt-0.5">
                {step === 'search' ? 'Find an available number' : 'Confirm and provision'}
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>

        {!hideCampaignWarning && !campaignReady && step === 'search' && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-300">
            <div className="flex items-start gap-2">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                Campaign isn&apos;t approved yet. You can still order a number — it will be
                attached to the campaign automatically once approval lands. The number will not
                send under A2P rules until then.
              </span>
            </div>
          </div>
        )}

        {step === 'search' ? (
          <div className="space-y-4">
            {/* Search form */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-[120px_120px_1fr_auto]">
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                  Type
                </Label>
                <Select value={type} onValueChange={(v: 'local' | 'tollfree') => setType(v)}>
                  <SelectTrigger className="h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="local">Local</SelectItem>
                    <SelectItem value="tollfree">Toll-free</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                  Area code
                </Label>
                <Input
                  value={areaCode}
                  onChange={(e) => setAreaCode(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  placeholder="415"
                  inputMode="numeric"
                  maxLength={3}
                  className="h-9 font-mono tabular-nums"
                  disabled={type === 'tollfree'}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                  Starts with
                </Label>
                <Input
                  value={contains}
                  onChange={(e) => setContains(e.target.value.replace(/\D/g, '').slice(0, 7))}
                  placeholder="optional"
                  inputMode="numeric"
                  className="h-9 font-mono tabular-nums"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-transparent">
                  &nbsp;
                </Label>
                <Button
                  size="sm"
                  onClick={handleSearch}
                  disabled={isSearching || (type === 'local' && !areaCode && !contains)}
                  className="h-9 bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
                >
                  {isSearching ? (
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Search className="mr-1.5 h-3.5 w-3.5" />
                  )}
                  Search
                </Button>
              </div>
            </div>

            {/* Results */}
            <div className="min-h-[280px] rounded-xl border border-slate-200 bg-slate-50/50 dark:border-slate-800 dark:bg-slate-950/30">
              {isSearching ? (
                <div className="flex h-[280px] flex-col items-center justify-center text-slate-400">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <p className="mt-2 text-xs">Querying Telnyx...</p>
                </div>
              ) : searchError ? (
                <div className="flex h-[280px] flex-col items-center justify-center px-6 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-rose-50 text-rose-500 dark:bg-rose-950/40">
                    <AlertCircle className="h-5 w-5" />
                  </div>
                  <p className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
                    Search failed
                  </p>
                  <p className="mt-1 max-w-md text-xs text-slate-500">{searchError.message}</p>
                </div>
              ) : !hasSearched ? (
                <div className="flex h-[280px] flex-col items-center justify-center px-6 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-slate-400 ring-1 ring-slate-200 dark:bg-slate-900 dark:ring-slate-700">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <p className="mt-3 text-sm font-medium text-slate-700 dark:text-slate-200">
                    Enter criteria above to search
                  </p>
                  <p className="mt-1 max-w-sm text-xs text-slate-500">
                    Filter by area code or pattern. Toll-free returns 8XX numbers regardless of
                    area code.
                  </p>
                </div>
              ) : searchResults.length === 0 ? (
                <div className="flex h-[280px] flex-col items-center justify-center px-6 text-center">
                  <p className="text-sm text-slate-500">No matching numbers right now.</p>
                  <p className="mt-1 max-w-sm text-xs text-slate-400">
                    Telnyx&apos;s pool changes constantly. Try a neighbouring area code or
                    different pattern.
                  </p>
                </div>
              ) : (
                <ul className="divide-y divide-slate-200 dark:divide-slate-800">
                  {searchResults.map((num) => (
                    <li key={num.number}>
                      <button
                        type="button"
                        onClick={() => handleSelect(num)}
                        className="grid w-full grid-cols-[1fr_auto_auto] items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-white dark:hover:bg-slate-900"
                      >
                        <div>
                          <div className="font-mono text-[15px] font-semibold tabular-nums text-slate-900 dark:text-white">
                            {formatE164(num.number)}
                          </div>
                          <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-slate-500">
                            {(num.locality || num.region) && (
                              <>
                                <MapPin className="h-3 w-3" />
                                <span>
                                  {[num.locality, num.region].filter(Boolean).join(', ')}
                                </span>
                                <span className="text-slate-300 dark:text-slate-600">·</span>
                              </>
                            )}
                            <span className="uppercase tracking-wide">
                              {num.type === 'tollfree' ? 'Toll-free' : 'Local'}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] font-bold uppercase text-slate-400">
                          {num.capabilities.sms && (
                            <span className="rounded bg-sky-50 px-1.5 py-0.5 text-sky-700 ring-1 ring-sky-200/60 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800/50">
                              SMS
                            </span>
                          )}
                          {num.capabilities.mms && (
                            <span className="rounded bg-sky-50 px-1.5 py-0.5 text-sky-700 ring-1 ring-sky-200/60 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800/50">
                              MMS
                            </span>
                          )}
                          {num.capabilities.voice && (
                            <span className="rounded bg-sky-50 px-1.5 py-0.5 text-sky-700 ring-1 ring-sky-200/60 dark:bg-sky-950/40 dark:text-sky-300 dark:ring-sky-800/50">
                              VOICE
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          {num.monthlyPrice !== undefined ? (
                            <>
                              <div className="font-mono text-sm tabular-nums text-slate-700 dark:text-slate-200">
                                ${num.monthlyPrice.toFixed(2)}
                              </div>
                              <div className="text-[10px] uppercase text-slate-400">/ mo</div>
                            </>
                          ) : (
                            <span className="text-[10px] uppercase tracking-wide text-slate-400">
                              Standard
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : (
          // ─── Confirm step ────────────────────────────────────────────
          <div className="space-y-5">
            <div className="rounded-xl border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-5 dark:border-slate-800 dark:from-slate-900/40 dark:to-slate-950/40">
              <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                Selected
              </p>
              <div className="mt-1 flex items-baseline gap-3">
                <span className="font-mono text-2xl font-semibold tabular-nums text-slate-900 dark:text-white">
                  {selected ? formatE164(selected.number) : ''}
                </span>
                {selected?.monthlyPrice !== undefined && (
                  <span className="font-mono text-sm tabular-nums text-slate-500">
                    ${selected.monthlyPrice.toFixed(2)} / mo
                  </span>
                )}
              </div>
              {(selected?.locality || selected?.region) && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                  <MapPin className="h-3 w-3" />
                  <span>
                    {[selected?.locality, selected?.region].filter(Boolean).join(', ')}
                  </span>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] uppercase tracking-wider text-slate-500">
                  Friendly name <span className="lowercase tracking-normal text-slate-400">(optional)</span>
                </Label>
                <Input
                  value={friendlyName}
                  onChange={(e) => setFriendlyName(e.target.value)}
                  placeholder="e.g. Marketing line, Sales line"
                  className="h-10"
                />
              </div>

              <div
                className={cn(
                  'flex items-center justify-between rounded-lg border px-4 py-3',
                  'border-slate-200 dark:border-slate-800',
                )}
              >
                <div>
                  <p className="text-sm font-medium text-slate-900 dark:text-white">
                    Set as default sender
                  </p>
                  <p className="text-xs text-slate-500">
                    Outbound campaigns and replies will use this number first.
                  </p>
                </div>
                <Switch
                  checked={setAsDefault}
                  onCheckedChange={setAsDefaultState}
                />
              </div>
            </div>

            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-xs text-emerald-800 dark:border-emerald-800/50 dark:bg-emerald-950/30 dark:text-emerald-300">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  After provisioning, this number will be assigned to the tenant&apos;s messaging
                  profile
                  {selected?.type === 'tollfree'
                    ? '. Submit Toll-Free Verification next so it can deliver at production volume.'
                    : campaignReady
                      ? ' and 10DLC campaign. Outbound traffic for the tenant will start routing through it immediately.'
                      : '. Outbound traffic for the tenant will start routing through it immediately.'}
                </span>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          {step === 'search' ? (
            <Button variant="outline" onClick={handleClose}>
              Cancel
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={() => setStep('search')}>
                Back
              </Button>
              <Button
                onClick={handleProvision}
                disabled={isProvisioning}
                className="bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
              >
                {isProvisioning ? (
                  <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
                ) : (
                  <Phone className="mr-1.5 h-4 w-4" />
                )}
                {isProvisioning ? 'Provisioning...' : 'Provision number'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
